import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Box } from "ds/Box";
import { FormItem } from "ds/FormItem";
import { Modal } from "ds/Modal";
import { TextInput } from "ds/TextInput";
import { Text } from "ds/Typography";
import { useGlobalContext } from "hooks";
import { useModalContext } from "hooks/useModalContext";
import { useSelectionContext } from "hooks/useSelectionContext";
import { notify } from "lib/notification";
import { getToken } from "utils/API";

export const ConvertToAppModal = () => {
  const { numberUuid } = useGlobalContext();
  const { convertToApp, updateModal } = useModalContext();
  const { selectedItems, setSelectedItems } = useSelectionContext();
  const { t } = useTranslation();

  const [appName, setAppName] = useState("");
  const [appVersion, setAppVersion] = useState("1.0.0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onClose = () => {
    updateModal("convertToApp", false);
    setAppName("");
    setAppVersion("1.0.0");
    setError(null);
  };

  const onConfirm = async () => {
    if (!appName.trim()) {
      setError(t("content.stack-containers.convert-to-app-modal.app-name-required"));
      return;
    }

    if (!appVersion.trim()) {
      setError(t("content.stack-containers.convert-to-app-modal.app-version-required"));
      return;
    }

    if (!selectedItems || selectedItems.length === 0) {
      setError(t("content.stack-containers.convert-to-app-modal.select-journey-required"));
      return;
    }

    setError(null);

    setLoading(true);

    try {
      const csrfToken = await getToken();
      const response = await fetch("/apps/convert-from-journeys", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify({
          number_uuid: numberUuid,
          journey_uuids: selectedItems,
          app_name: appName.trim(),
          app_version: appVersion.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t("content.stack-containers.convert-to-app-modal.conversion-failed"));
      }

      const result = await response.json();

      notify({
        type: "success",
        position: "bottom-right",
        title: t("content.stack-containers.convert-to-app-modal.app-created-success"),
        description: t("content.stack-containers.convert-to-app-modal.app-created-description", {
          name: result.app_name,
          version: result.app_version,
        }),
        autoClose: 5000,
      });

      // Clear selection and close modal
      setSelectedItems(undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("content.stack-containers.convert-to-app-modal.unknown-error"));
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = selectedItems?.length || 0;

  return (
    <Modal
      type="STEPS"
      visible={convertToApp}
      title={t("content.stack-containers.convert-to-app-modal.title")}
      onHide={onClose}
      style={{ width: "500px" }}
      primaryAction={{
        text: loading
          ? t("content.stack-containers.convert-to-app-modal.creating-app")
          : t("content.stack-containers.convert-to-app-modal.create-app"),
        onClick: onConfirm,
        disabled: loading,
      }}
      secondaryAction={{
        text: t("global.cancel"),
        onClick: onClose,
      }}
    >
      <Box gap="300">
        <Text preset="paragraph1-100">
          {t("content.stack-containers.convert-to-app-modal.description", { count: selectedCount })}
        </Text>

        {error && (
          <Box backgroundColor="redLightest" color="redDarkest" px="150" py="100" borderRadius="200">
            <Text preset="paragraph1-100">{error}</Text>
          </Box>
        )}

        <FormItem label={t("content.stack-containers.convert-to-app-modal.app-name-label")}>
          <TextInput
            placeholder={t("content.stack-containers.convert-to-app-modal.app-name-placeholder")}
            value={appName}
            onChange={(e) => {
              setAppName(e.target.value);
              if (error) {
                setError(null);
              }
            }}
          />
        </FormItem>

        <FormItem label={t("content.stack-containers.convert-to-app-modal.version-label")}>
          <TextInput
            placeholder={t("content.stack-containers.convert-to-app-modal.version-placeholder")}
            value={appVersion}
            onChange={(e) => {
              setAppVersion(e.target.value);
              if (error) {
                setError(null);
              }
            }}
          />
        </FormItem>
      </Box>
    </Modal>
  );
};
